import React, { useState } from "react";
import { Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Flx from "../../../components/layout/Flx";
import colors from "../../../theme/colors";
import formatDate from "../../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import formatPhone from "../../../_src_shared/utils/functions/formatPhone";
import { formatSsn } from "../../../_src_shared/utils/functions/formatSsn";
import SubmissionDocumentsSection from "./SubmissionDocumentsSection";
import {
  LANES,
  formatPercentValue,
  maskSSN,
  submissionBorrower,
  submissionBorrowerName,
  submissionDocumentCount,
  submissionEmail,
  submissionEntity,
  submissionPhone,
  submissionSignedBy,
} from "../submissionHelpers";

const dateOnly = (v) => formatDate(v, null, "MMM D, YYYY");
const dateTime = (v) => formatDate(v, null, "MMM D, YYYY h:mm A");
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : null);
const money = (v) => (v == null || v === "" ? null : formatDollar(v));

/** A ratio of two figures as a percentage, or null when either is missing. */
const ratio = (numerator, denominator) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  return formatPercentValue(n / d);
};

const SectionLabel = ({ children }) => (
  <Box
    sx={{
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: colors.slate500,
      pb: "10px",
    }}
  >
    {children}
  </Box>
);

/**
 * One key/value line. An absent value renders as an em dash in the muted weight
 * rather than being hidden — on a submission review screen "we didn't ask" and
 * "they left it blank" both matter, and a row that vanishes tells you neither.
 */
const Row = ({ label, value, emphasis }) => {
  const empty = value === null || value === undefined || value === "";
  return (
    <Flx
      between
      gap={2}
      sx={{
        py: "8px",
        borderBottom: `1px solid ${colors.slate100}`,
        fontSize: 13,
      }}
    >
      <Box sx={{ color: colors.slate500, flexShrink: 0 }}>{label}</Box>
      <Box
        sx={{
          fontWeight: empty ? 400 : emphasis ? 700 : 550,
          color: empty
            ? colors.slate400
            : emphasis
              ? "primary.dark"
              : "inherit",
          textAlign: "right",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {empty ? "—" : value}
      </Box>
    </Flx>
  );
};

/**
 * Sections stacked in the same column get a rule and a wide gap above them.
 * Without it the last row of one section and the heading of the next were only
 * a hairline apart, so the columns read as one long list rather than as blocks.
 */
/**
 * The SSN, masked until clicked.
 *
 * Masked by DEFAULT rather than on demand: this panel gets read with people
 * looking over a shoulder and gets screen-shared, and a full SSN sitting on
 * screen for everyone who expands a row is the wrong default. Revealing is one
 * click and it re-masks on the next, so the number stays available without
 * being ambient.
 *
 * Only interactive when the digits are actually here — the list read strips
 * `borrowerSSN`, so a row showing "Provided · not shown here" has nothing to
 * reveal and shouldn't pretend otherwise.
 */
const SsnValue = ({ ssn }) => {
  const [shown, setShown] = useState(false);
  if (!ssn) return null;

  return (
    <Flx
      centerY
      gap={0.75}
      onClick={() => setShown((v) => !v)}
      title={shown ? "Hide SSN" : "Show SSN"}
      sx={{
        cursor: "pointer",
        justifyContent: "flex-end",
        fontFamily: shown ? '"SF Mono", ui-monospace, Menlo, monospace' : undefined,
        "&:hover": { color: "primary.main" },
      }}
    >
      {shown ? formatSsn(ssn, true) : maskSSN(ssn)}
      {shown ? (
        <VisibilityOffOutlinedIcon sx={{ fontSize: 15, opacity: 0.7 }} />
      ) : (
        <VisibilityOutlinedIcon sx={{ fontSize: 15, opacity: 0.7 }} />
      )}
    </Flx>
  );
};

const Section = ({ title, rows, first }) => (
  <Box
    // sx={
    //   first
    //     ? undefined
    //     : {
    //         mt: 4,
    //         pt: 4,
    //         borderTop: `1px solid ${colors.slate200}`,

    //       }
    // }
    sx={{
      border: `1px solid ${colors.slate200}`,
      p: 1.5,
      pb: 0,
      borderRadius: 1,
      mt: first ? 0 : 2,
      // pt: first ? 0 : 4,
    }}
  >
    <SectionLabel>{title}</SectionLabel>
    {Array.isArray(rows)
      ? rows.map(([label, value, emphasis]) => (
          <Row key={label} label={label} value={value} emphasis={emphasis} />
        ))
      : rows}
  </Box>
);

/**
 * The submission at a glance, in three columns.
 *
 * The field set is what a loan officer actually needs off an intake before
 * deciding whether to work the deal — not just identity. That means the credit
 * box (FICO, citizenship, liquidity), the deal shape (product, purpose, exit,
 * broker), the entity, the collateral economics, and the sizing ratios derived
 * from them.
 *
 * Ratios are COMPUTED here rather than read: the submission carries purchase
 * price, budget, as-is and ARV but no LTC/LTARV, and those are the two numbers
 * that decide a fix-and-flip. They're marked as derived in their labels so
 * nobody mistakes them for borrower-stated figures.
 *
 * SSN and date of birth are in `LIST_SENSITIVE_FIELDS` on the backend, so the
 * grid's list read strips them; SSN falls back to the `borrowerHasSSN` flag so
 * "we have one" stays distinguishable from "never given". SSN is masked either
 * way — a full one should never reach the screen.
 */
const SubmissionSummaryTab = ({ row }) => {
  if (!row) return null;

  const b = submissionBorrower(row);
  const isApplication = row.__type === LANES.LOAN_APPLICATION;
  const property = row.subjectProperties?.[0] || {};
  const entity = submissionEntity(row);
  const link = row.linkSummary || {};

  // The digits when the detail read supplied them (click to reveal), otherwise
  // the honest fallback: the list read strips borrowerSSN but keeps the flag, so
  // "we hold one" stays distinguishable from "never given".
  const ssn = b.borrowerSSN ? (
    <SsnValue ssn={b.borrowerSSN} />
  ) : b.borrowerHasSSN ? (
    row.__fullRecord ? "Has one · number not submitted" : "Provided · not shown here"
  ) : null;

  const borrowerSection = [
    ["Name", submissionBorrowerName(row)],
    ["Email", submissionEmail(row)],
    ["Phone", formatPhone(String(submissionPhone(row) ?? "")) || null],
    ["Citizenship", b.citizenshipStatus],
    ["Marital status", b.maritalStatus],
    ["Date of birth", dateOnly(b.dateOfBirth)],
    ["SSN", ssn],
    ["Estimated FICO", b.estimatedFICO],
    ["Cash liquidity", money(b.cashLiquidity ?? row.cashLiquidity)],
    ["Other liquidity", money(b.otherLiquidity ?? row.otherLiquidity)],
    ["Primary residence", row.primaryResidenceOwnership],
    ["First-time buyer", yesNo(row.firstTimeHomebuyer)],
    ["Home address", b.primaryAddress?.fullAddress],
  ];

  // Read through submissionEntity, not off the row: the entity fields arrive
  // either inline or inside entities[0] depending on the submission, and reading
  // one side blanked Type / State of organization / EIN on the other.
  const entitySection = [
    ["Entity name", entity.entityName],
    ["Existing entity", yesNo(row.applicantHasExistingEntity)],
    ["Type", entity.entityType],
    ["EIN", entity.entityEINNumber],
    ["State of organization", entity.entityStateOfOrganization],
    ["Entity address", entity.entityAddress?.fullAddress],
    [
      "Ownership",
      formatPercentValue((row.applicantEntityPercentOwnership ?? 0) / 100),
    ],
    ["Entity holds assets", yesNo(row.entityAssets)],
  ];

  const loanSection = [
    ["Purpose", row.loanPurpose],
    ["Product", row.loanProductType],
    ["Base amount", money(row.baseLoanAmount)],
    ["Exit strategy", row.exitStrategy],
    ["Expected closing", dateOnly(row.expectedClosingDate)],
    ["Broker deal", yesNo(row.brokerDeal)],
    ["Properties", row.subjectPropertyCount],
  ];

  // The collateral, plus the two ratios that size a fix-and-flip. Total cost is
  // purchase + rehab, which is what loan-to-cost is measured against.
  const totalCost =
    property.purchasePrice != null
      ? Number(property.purchasePrice) +
        Number(property.constructionBudget || 0)
      : null;

  const propertySection = [
    ["Address", property.address?.fullAddress],
    ["Type", property.propertyType],
    ["Purchase price", money(property.purchasePrice)],
    ["Purchase date", dateOnly(property.purchaseDate)],
    ["As-is value", money(property.asIsValue)],
    ["ARV", money(property.arv)],
    ["Rehab budget", money(property.constructionBudget)],
    [
      "Budget financed",
      property.financedBudgetPercent != null
        ? `${property.financedBudgetPercent}%`
        : null,
    ],
    ["Total cost", money(totalCost)],
    ["Loan to cost", ratio(row.baseLoanAmount, totalCost), true],
    ["Loan to ARV", ratio(row.baseLoanAmount, property.arv), true],
    ["Loan to as-is", ratio(row.baseLoanAmount, property.asIsValue)],
  ];

  const creditPullSection = [
    ["Pulled", dateTime(row.creditPullDate)],
    ["Pulled by", row.pulledBy],
    ["Result", row.pullResult],
    ["Report id", row.reportId],
  ];

  // Where the submission came from and who is accountable for it — the link's
  // owner is the salesperson it's attributed to.
  const submissionSection = [
    ["Submitted", dateTime(row.submission_date)],
    ["Signed by", submissionSignedBy(row)],
    ["Signed at", dateTime(row.attestation?.signed_at)],
    ["Client IP", row.client_ip || row.attestation?.client_ip],
    ["Sent by", link.owner_email || row.created_by],
    ["Sent to", link.recipient_email],
    ["Link created", dateOnly(link.created_date)],
  ];

  // Assigned explicitly rather than flowed, so the columns stay balanced and a
  // section never splits across two of them.
  // A section body can be a node instead of rows — the documents list is one.
  const documentsSection = <SubmissionDocumentsSection submission={row} />;

  const columns = isApplication
    ? [
        [["Borrower", borrowerSection]],
        [
          ["Loan terms", loanSection],
          ["Entity", entitySection],
        ],
        [
          ["Subject property", propertySection],
          [`Documents (${submissionDocumentCount(row)})`, documentsSection],
          ["Submission", submissionSection],
        ],
      ]
    : [
        [["Borrower", borrowerSection]],
        [
          ["Credit pull", creditPullSection],
          ["Entity", entitySection],
        ],
        [
          [`Documents (${submissionDocumentCount(row)})`, documentsSection],
          ["Submission", submissionSection],
        ],
      ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(3, 1fr)" },
        columnGap: 3,
        rowGap: 3,
        // columnGap: "56px",
        // rowGap: "30px",
      }}
    >
      {columns.map((column, i) => (
        <Box key={i} sx={{ minWidth: 0 }}>
          {column.map(([title, rows], j) => (
            <Section title={title} rows={rows} first={j === 0} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default SubmissionSummaryTab;
